import { getToday } from "@/utils/dateUtils";

/**
 * Hook that provides date constraints for budget planning
 * Returns current and next month information and validation helpers
 */
export const useDateConstraints = () => {
  const today = getToday();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 0-indexed to 1-indexed
  
  // Calculate next month
  const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
  const nextMonthYear = currentMonth === 12 ? currentYear + 1 : currentYear;
  
  // Formatted versions
  const currentMonthFormatted = currentMonth.toString().padStart(2, '0');
  const currentYearFormatted = currentYear.toString();
  const nextMonthFormatted = nextMonth.toString().padStart(2, '0');
  const nextMonthYearFormatted = nextMonthYear.toString();
  
  /**
   * Check if the provided year/month is valid (not beyond next month)
   */
  const isDateValid = (year: number, month: number): boolean => {
    return !(
      (year > nextMonthYear) || 
      (year === nextMonthYear && month > nextMonth)
    );
  };
  
  /**
   * Check if the provided year/month is the current month
   */
  const isCurrentMonth = (year: number, month: number): boolean => {
    return year === currentYear && month === currentMonth;
  };
  
  /**
   * Check if the provided year/month is next month
   */
  const isNextMonth = (year: number, month: number): boolean => {
    return year === nextMonthYear && month === nextMonth;
  };
  
  /**
   * Check if editing is allowed for the provided year/month
   */
  const canEdit = (year: number, month: number): boolean => {
    return isCurrentMonth(year, month) || isNextMonth(year, month);
  };
  
  return {
    currentYear,
    currentMonth,
    currentMonthFormatted,
    currentYearFormatted,
    nextMonth,
    nextMonthYear,
    nextMonthFormatted,
    nextMonthYearFormatted,
    isDateValid,
    isCurrentMonth,
    isNextMonth,
    canEdit,
    maxDate: [nextMonthYearFormatted, nextMonthFormatted] as [string, string]
  };
}; 